import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star, ArrowRight, ChevronLeft, ChevronRight, Heart } from 'lucide-react';
import VendorServiceSkeleton from '../shared/VendorServiceSkeleton';

const VendorServices = ({ 
  title = 'Vendor Services',
  data = [],
  isLoading = false,
  error = null,
  emptyMessage = 'No services available',
  emptyDescription = 'Check back later for services',
  linkPrefix = '/services',
  priceLabel = 'starting from',
  showArrowInTitle = true,
  className = '',
  categorySlug = null, // For linking to category page
  viewAllLink = null // Custom link for "view all"
}) => {
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const vendorServices = data;

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 400;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Check initial scroll position
  useEffect(() => {
    handleScroll();
    if (scrollRef.current) {
      const resizeObserver = new ResizeObserver(() => handleScroll());
      resizeObserver.observe(scrollRef.current);
      return () => resizeObserver.disconnect();
    }
  }, [vendorServices.length]);

  return (
    <section className={`py-8 bg-white relative overflow-hidden ${className}`}>
      <div className="page-container relative z-10">
        {/* Section Header with Navigation Arrows */}
        <div className="flex items-center justify-between mb-6">
          {categorySlug || viewAllLink ? (
            <Link
              to={viewAllLink || `/category/${categorySlug}?title=${encodeURIComponent(title)}`}
              className="text-xl font-medium text-gray-900 flex items-center hover:text-gray-700 transition-colors group"
            >
              {title}
              {showArrowInTitle && (
                <ArrowRight className="w-5 h-5 ml-2 text-gray-600 group-hover:translate-x-1 transition-transform" />
              )}
            </Link>
          ) : (
            <h2 className="text-xl font-medium text-gray-900 flex items-center">
              {title}
              {showArrowInTitle && <ArrowRight className="w-5 h-5 ml-2 text-gray-600" />}
            </h2>
          )}
          
          {/* Navigation Arrows - Top Right */}
          {!isLoading && !error && vendorServices.length > 0 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => scroll('left')}
                disabled={!showLeftArrow}
                className={`rounded-full p-1.5 transition-all border ${
                  showLeftArrow
                    ? 'bg-white border-gray-300 hover:border-gray-400 text-gray-700 hover:shadow-md'
                    : 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                }`}
                aria-label="Scroll left"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <button
                onClick={() => scroll('right')}
                disabled={!showRightArrow}
                className={`rounded-full p-1.5 transition-all border ${
                  showRightArrow
                    ? 'bg-white border-gray-300 hover:border-gray-400 text-gray-700 hover:shadow-md'
                    : 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                }`}
                aria-label="Scroll right"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {isLoading ? (
          <VendorServiceSkeleton count={6} />
        ) : error ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <div className="text-red-500 text-lg font-medium mb-2">Failed to load services</div>
              <p className="text-gray-600">Please try again later</p>
            </div>
          </div>
        ) : vendorServices.length === 0 ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <div className="text-gray-500 text-lg font-medium mb-2">{emptyMessage}</div>
              <p className="text-gray-600">{emptyDescription}</p>
            </div>
          </div>
        ) : (
          <div className="relative">

            {/* Horizontal Scrolling Carousel */}
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="flex gap-4 overflow-x-auto scrollbar-hide pb-4 scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {vendorServices.map((service) => (
                <Link
                  key={service.id}
                  to={`${linkPrefix}/${service.id}`}
                  className="flex-shrink-0 w-[calc((100%-5*1rem)/6)] min-w-[180px] group"
                >
                  <div className="relative rounded-2xl overflow-hidden">
                    {/* Image Container */}
                    <div className="relative h-48 bg-gray-200 overflow-hidden rounded-2xl">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      
                      {/* Guest Favorite Badge */}
                      {service.isFeatured && (
                        <div className="absolute top-3 left-3 bg-gray-900/80 text-white px-2.5 py-1 rounded-md text-xs font-medium">
                          Featured
                        </div>
                      )}

                      {/* Heart Icon */}
                      {/* <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        className="absolute top-3 right-3 p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors opacity-0 group-hover:opacity-100"
                        aria-label="Add to favorites"
                      >
                        <Heart className="w-4 h-4 text-gray-700" />
                      </button> */}
                    </div>

                    {/* Card Content */}
                    <div className="mt-3">
                      {/* Title and Rating */}
                      <div className="flex items-start justify-between mb-1">
                        <h3 className="text-sm font-semibold text-gray-900 line-clamp-1 flex-1">
                          {service.title}
                        </h3>
                        <div className="flex items-center ml-2 flex-shrink-0">
                          <Star className="w-3 h-3 fill-current text-gray-900" />
                          <span className="text-xs font-medium text-gray-900 ml-1">
                            {service.rating > 0 ? Number(service.rating).toFixed(2) : 'New'}
                          </span>
                        </div>
                      </div>

                      {/* Location */}
                      <p className="text-xs text-gray-500 mb-2 line-clamp-1">
                        {service.location}
                      </p>

                      {/* Price */}
                      <div className="flex items-center">
                        <span className="text-sm font-semibold text-gray-900">
                          {service.price > 0 ? (
                            `$${Number(service.price).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
                          ) : (
                            'Contact'
                          )}
                        </span>
                        <span className="text-xs text-gray-600 ml-1">{priceLabel}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default VendorServices;

